package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.Image
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.pages.ContactsViewModel
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p14
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun ContactsView(viewModel: ContactsViewModel) {
    // Wide desktop layout only on landscape Web; portrait Web + native use the mobile layout.
    val isWeb = rememberIsWebDesktopLayout()
    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = !isWeb,
        removeVerticalPadding = false
    ) { uiState ->
        if (isWeb) {
            Column(modifier = Modifier.fillMaxSize()) {
                ClientWebHeaderBar(
                    selectedTab = 3,
                    homeTabIndex = 0,
                    deliveryTabIndex = 1,
                    favoritesTabIndex = 2,
                    menuTabIndex = 3,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    showBackButton = true,
                    onBackClick = viewModel::onBackClick,
                    centerTitle = sharedString(SR.strings.contacts)
                )
                WebHeaderBottomDivider()
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.TopCenter
                ) {
                    ContactsContent(
                        uiState = uiState,
                        viewModel = viewModel,
                        modifier = Modifier
                            .width(1080.dp)
                            .padding(horizontal = p16, vertical = p32),
                        contactCardWidthFraction = 0.5f
                    )
                }
            }
        } else {
            ContactsContent(
                uiState = uiState,
                viewModel = viewModel,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = p16, vertical = p32)
            )
        }
    }
}

@Composable
private fun ContactsContent(
    uiState: androidx.compose.runtime.State<com.example.mystybag.appCore.model.pages.ContactsUIState>,
    viewModel: ContactsViewModel,
    modifier: Modifier,
    contactCardWidthFraction: Float = 1f
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(p16)
    ) {
        ContactCard(
            icon = SRC.images.contact_mail,
            title = sharedString(SR.strings.email),
            value = uiState.value.email,
            onClick = { viewModel.onEmailClicked() },
            widthFraction = contactCardWidthFraction
        )
        ContactCard(
            icon = SRC.images.contact_phone,
            title = sharedString(SR.strings.phone_number),
            value = uiState.value.phoneNumber,
            onClick = { viewModel.onPhoneClicked() },
            widthFraction = contactCardWidthFraction
        )
    }
}

@Composable
private fun ContactCard(
    icon: ImageResource,
    title: String,
    value: String,
    onClick: () -> Unit,
    widthFraction: Float = 1f
) {
    Card(
        modifier = Modifier
            .fillMaxWidth(widthFraction)
            .height(96.dp)
            .rippleClick(padding = p0, roundPadding = p12, action = onClick),
        shape = RoundedCornerShape(p12),
        colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
        border = BorderStroke(1.dp, AppTheme.current.colors.outline)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(20.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(p16),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    painter = painterResource(icon),
                    contentDescription = null,
                    modifier = Modifier.size(24.dp)
                )
                Column(verticalArrangement = Arrangement.spacedBy(p8)) {
                    Texts.TitleMedium(
                        text = title,
                        color = AppTheme.current.colors.onSurface
                    )
                    Texts.BodyLarge(
                        text = value,
                        color = AppTheme.current.colors.primary
                    )
                }
            }
            Image(
                painter = painterResource(SRC.images.combobox_arrow_right_green),
                contentDescription = null,
                modifier = Modifier.size(width = p8, height = p14),
                colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurfaceVariant)
            )
        }
    }
}
